import React, { useState } from 'react';
import { Eye, EyeOff, Lock, Unlock, Plus, Trash2, GripVertical, ChevronLeft, ChevronRight } from 'lucide-react';
import { ArchitectureNode } from '../types';

interface Layer {
  id: string;
  name: string;
  visible: boolean;
  locked: boolean;
  zIndex: number;
}

interface LayersPanelProps {
  isOpen: boolean;
  onClose: () => void;
  nodes: ArchitectureNode[];
  layers: Layer[];
  onLayersChange: (layers: Layer[]) => void;
  onNodeLayerChange: (nodeId: string, layerId: string | null) => void;
  onToggleLayerVisibility: (layerId: string) => void;
  onToggleLayerLock: (layerId: string) => void;
}

const LayersPanel: React.FC<LayersPanelProps> = ({
  isOpen,
  onClose,
  nodes,
  layers,
  onLayersChange,
  onNodeLayerChange,
  onToggleLayerVisibility,
  onToggleLayerLock
}) => {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [editingLayerId, setEditingLayerId] = useState<string | null>(null);
  const [editingName, setEditingName] = useState('');

  const handleCreateLayer = () => {
    const newLayer: Layer = {
      id: `layer-${Date.now()}`,
      name: `Layer ${layers.length + 1}`,
      visible: true,
      locked: false,
      zIndex: layers.length
    };
    onLayersChange([...layers, newLayer]);
  };

  const handleDeleteLayer = (layerId: string) => {
    if (window.confirm('Delete this layer? Components will be moved to default layer.')) {
      // Move nodes to default (no layer)
      nodes.forEach(node => {
        if (node.data.layerId === layerId) {
          onNodeLayerChange(node.id, null);
        }
      });
      onLayersChange(layers.filter(l => l.id !== layerId).map((l, idx) => ({ ...l, zIndex: idx })));
    }
  };

  const handleRenameLayer = (layerId: string) => {
    const layer = layers.find(l => l.id === layerId);
    if (layer) {
      setEditingLayerId(layerId);
      setEditingName(layer.name);
    }
  };

  const handleSaveRename = () => {
    if (editingLayerId && editingName.trim()) {
      onLayersChange(layers.map(l => 
        l.id === editingLayerId ? { ...l, name: editingName.trim() } : l
      ));
    }
    setEditingLayerId(null);
    setEditingName('');
  };

  const handleMoveLayer = (layerId: string, direction: 'up' | 'down') => {
    const index = layers.findIndex(l => l.id === layerId);
    if (index === -1) return;
    
    const newIndex = direction === 'up' ? index - 1 : index + 1;
    if (newIndex < 0 || newIndex >= layers.length) return;
    
    const newLayers = [...layers];
    [newLayers[index], newLayers[newIndex]] = [newLayers[newIndex], newLayers[index]];
    onLayersChange(newLayers.map((l, idx) => ({ ...l, zIndex: idx })));
  };

  const getNodeCount = (layerId: string) => {
    return nodes.filter(n => n.data.layerId === layerId).length;
  };

  if (isCollapsed) {
    return (
      <div className="w-12 bg-white border-l border-black/10 h-full flex flex-col items-center py-4 relative shrink-0 transition-all duration-300 ease-in-out will-change-width">
        <button 
          onClick={() => setIsCollapsed(false)} 
          className="p-2 hover:bg-gray-100 rounded-md mb-4 text-gray-500 hover:text-black transition-colors"
          title="Expand Panel"
        >
          <ChevronLeft size={20} />
        </button>
        <div className="flex-1 flex items-center justify-center">
          <div className="rotate-90 whitespace-nowrap text-xs font-bold tracking-widest text-gray-400 uppercase flex items-center gap-2">
            <Eye size={14} />
            Layers
          </div>
        </div>
      </div>
    );
  }

  if (!isOpen) return null;

  return (
    <div className="w-80 bg-white border-l border-black/10 flex flex-col h-full shadow-xl shrink-0 animate-slide-in-right will-change-transform" style={{ animationDuration: '300ms' }}>
      <div className="p-4 border-b border-black/10 flex justify-between items-center bg-white h-[69px]">
        <div className="flex items-center gap-2">
          <button 
            onClick={() => setIsCollapsed(true)} 
            className="p-1 hover:bg-gray-100 rounded text-gray-400 hover:text-black transition-colors"
            title="Collapse Panel"
          >
            <ChevronRight size={18} />
          </button>
          <h2 className="font-bold text-black">Layers</h2>
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-black transition-colors" title="Close">
          <ChevronRight size={18} />
        </button>
      </div>

      <div className="p-4 border-b border-black/10">
        <button
          onClick={handleCreateLayer}
          className="w-full flex items-center justify-center gap-2 bg-black text-white px-4 py-2 rounded-md text-sm font-medium hover:bg-gray-800 transition-colors"
        >
          <Plus size={16} />
          Create Layer
        </button>
      </div>

      <div className="flex-1 overflow-y-auto p-4 space-y-2">
        {/* Default Layer (no layer) */}
        <div className="bg-gray-50 border border-gray-200 rounded-md p-3">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">Default</span>
            <span className="text-xs text-gray-500">{nodes.filter(n => !n.data.layerId).length} components</span>
          </div>
        </div>

        {/* User Layers */}
        {layers.map((layer, index) => (
          <div
            key={layer.id}
            className="bg-white border-2 border-gray-200 rounded-md p-3 hover:border-gray-300 transition-colors"
            style={{ zIndex: layer.zIndex }}
          >
            <div className="flex items-center gap-2 mb-2">
              <GripVertical size={14} className="text-gray-400 cursor-move" />
              
              {editingLayerId === layer.id ? (
                <input
                  type="text"
                  value={editingName}
                  onChange={(e) => setEditingName(e.target.value)}
                  onBlur={handleSaveRename}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') handleSaveRename();
                    if (e.key === 'Escape') {
                      setEditingLayerId(null);
                      setEditingName('');
                    }
                  }}
                  className="flex-1 text-sm font-medium border border-gray-300 rounded px-2 py-1 outline-none focus:ring-1 focus:ring-black"
                  autoFocus
                />
              ) : (
                <span
                  className="flex-1 text-sm font-medium text-black cursor-pointer"
                  onDoubleClick={() => handleRenameLayer(layer.id)}
                >
                  {layer.name}
                </span>
              )}
              
              <div className="flex items-center gap-1">
                <button
                  onClick={() => onToggleLayerVisibility(layer.id)}
                  className={`p-1 rounded transition-colors ${layer.visible ? 'text-gray-600 hover:text-black' : 'text-gray-300 hover:text-gray-500'}`}
                  title={layer.visible ? 'Hide Layer' : 'Show Layer'}
                >
                  {layer.visible ? <Eye size={14} /> : <EyeOff size={14} />}
                </button>
                <button
                  onClick={() => onToggleLayerLock(layer.id)}
                  className={`p-1 rounded transition-colors ${layer.locked ? 'text-red-600 hover:text-red-700' : 'text-gray-400 hover:text-gray-600'}`}
                  title={layer.locked ? 'Unlock Layer' : 'Lock Layer'}
                >
                  {layer.locked ? <Lock size={14} /> : <Unlock size={14} />}
                </button>
                <button
                  onClick={() => handleDeleteLayer(layer.id)}
                  className="p-1 rounded text-gray-400 hover:text-red-600 transition-colors"
                  title="Delete Layer"
                >
                  <Trash2 size={14} />
                </button>
              </div>
            </div>
            
            <div className="flex items-center justify-between text-xs text-gray-500">
              <span>{getNodeCount(layer.id)} components</span>
              <div className="flex items-center gap-1">
                <button
                  onClick={() => handleMoveLayer(layer.id, 'up')}
                  disabled={index === 0}
                  className="p-1 rounded disabled:opacity-30 disabled:cursor-not-allowed hover:bg-gray-100"
                  title="Move Up"
                >
                  ↑
                </button>
                <button
                  onClick={() => handleMoveLayer(layer.id, 'down')}
                  disabled={index === layers.length - 1}
                  className="p-1 rounded disabled:opacity-30 disabled:cursor-not-allowed hover:bg-gray-100"
                  title="Move Down"
                >
                  ↓
                </button>
              </div>
            </div>
          </div>
        ))}

        {layers.length === 0 && (
          <div className="text-center py-8 text-gray-400 text-sm">
            <Eye size={32} className="mx-auto mb-2 opacity-50" />
            <p>No layers yet</p>
            <p className="text-xs mt-1">Create a layer to organize components</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default LayersPanel;
